import React, { useEffect, useState } from 'react';
import { Grid, Typography, Box, useTheme, Select, MenuItem, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend } from 'recharts';

import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';

const iconMapping = {
    Archived: ArchiveOutlinedIcon,
    ForVer: FactCheckOutlinedIcon,
    PendingPayment: PaymentsOutlinedIcon,
    PendingCode: VpnKeyOutlinedIcon,
    Confirmed: CheckCircleOutlineIcon,
    Rechecked: SyncOutlinedIcon,
    Cancelled: CancelOutlinedIcon
};

const StackedBarChartCard = ({
    props,
    palette,
    dashboardQuery,
    log,
    typeOfChart,
    pieOption = 'HealthCertifications',
    setPieOption,
    pieOptions = [],
    title = '',
    hideHeader = false,
    compact = false
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();

    const [barDataset, setBarDataset] = useState([]);
    const [pieDataset, setPieDataset] = useState([]);
    const [classes, setClasses] = useState([]);
    const [pieCenterLabel, setPieCenterLabel] = useState('');

    useEffect(() => {
        if (!props) return;

        if (typeOfChart === 'bar') {
            if (!props?.Data || props.Data.length === 0) return;

            const maxObject = props.Data.reduce((a, b) => (Object.keys(a).length > Object.keys(b).length ? a : b));
            const statusKeys = Object.keys(maxObject).filter(
                (key) => !['Datakey', 'LoggingDatakey', 'displayName', 'nav', 'value', 'name'].includes(key)
            );
            setClasses(statusKeys);

            const transformedBar = props.Data.map((datum) => ({
                ...datum,
                displayName: t(`${datum?.Datakey}`),
                nav:
                    datum?.Datakey === 'Athletes'
                        ? '/applications/athletes'
                        : datum?.Datakey === 'Officials'
                        ? '/applications/officials'
                        : datum?.Datakey === 'Clubs'
                        ? '/applications/clubs'
                        : null
            }));

            setBarDataset(transformedBar);
            return;
        }

        if (typeOfChart === 'pie') {
            if (!props?.Data || props.Data.length === 0) {
                setPieDataset([]);
                setPieCenterLabel('');
                return;
            }

            const selectedPie = props.Data.find((item) => item.Datakey === pieOption);

            if (!selectedPie) {
                setPieDataset([]);
                setPieCenterLabel('');
                return;
            }

            const transformedPie = [
                {
                    name: t('NotSet'),
                    value: Number(selectedPie.NotSet) || 0,
                    statusKey: 'NotSet',
                    datakey: selectedPie.Datakey
                },
                {
                    name: t('Expired'),
                    value: Number(selectedPie.Expired) || 0,
                    statusKey: 'Expired',
                    datakey: selectedPie.Datakey
                },
                {
                    name: t('ExpireSoon'),
                    value: Number(selectedPie.ExpireSoon) || 0,
                    statusKey: 'ExpireSoon',
                    datakey: selectedPie.Datakey
                },
                {
                    name: t('NotExpireSoon'),
                    value: Number(selectedPie.NotExpireSoon) || 0,
                    statusKey: 'NotExpireSoon',
                    datakey: selectedPie.Datakey
                }
            ];

            setPieDataset(transformedPie);
            setPieCenterLabel(selectedPie.Datakey || '');
        }
    }, [props, pieOption, typeOfChart, t]);

    const normalize = (value) =>
        String(value ?? '')
            .trim()
            .toLowerCase();

    const resolveExplorerFilterKey = (item) => {
        const nav = item?.nav ?? '';
        if (nav === '/applications/athletes') return 'AthletesApplications-Expl';
        if (nav === '/applications/officials') return 'OfficialsApplications-Expl';
        if (nav === '/applications/clubs') return 'ClubsApplications-Expl';
        return item?.LoggingDatakey || 'global';
    };

    const handleClick = (activeItem, index, seriesKey) => {
        if (!activeItem) return;

        const filterLabel = seriesKey || activeItem.statusKey;
        const translatedFilterLabel = t(filterLabel ?? '');
        const filterStatus = dashboardQuery?.data?.status?.find((s) => {
            const statusLabel = normalize(s?.label);
            return [normalize(filterLabel), normalize(translatedFilterLabel), normalize(activeItem?.name)].includes(statusLabel);
        });

        const logKey = resolveExplorerFilterKey(activeItem);
        const statusId = filterStatus?.value ?? null;
        const statusKey = filterLabel ?? '';
        const statusLabel = filterStatus?.label ?? activeItem?.name ?? translatedFilterLabel ?? filterLabel ?? '';

        if (log) {
            log(`explorer-${logKey}-filters`, {
                StatusId: statusId,
                Status: statusLabel,
                StatusKey: statusKey,
                selection: index,
                DateFrom: '',
                DateTo: ''
            });
        }

        if (activeItem.nav) {
            navigate(activeItem.nav);
        }
    };

    return (
        <Box
            boxShadow={compact ? 0 : 3}
            sx={{
                borderRadius: compact ? 0 : 2,
                p: compact ? 0 : 3,
                width: '100%',
                height: '100%',
                background: compact ? 'transparent' : theme.palette.background.paper,
                outline: 'none !important',
                '&:focus': { outline: 'none !important' },
                '&:active': { outline: 'none !important' },
                userSelect: 'none'
            }}
            tabIndex="-1"
        >
            {!hideHeader && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: compact ? 2 : 3 }}>
                    <Typography variant="h3" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
                        {title ? title : t(`${props?.Label}`)}
                    </Typography>

                    {typeOfChart === 'pie' && setPieOption && (
                        <Select size="small" value={pieOption} onChange={(e) => setPieOption(e.target.value)} sx={{ minWidth: 220 }}>
                            {pieOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                </Box>
            )}

            {typeOfChart === 'pie' && compact && (
                <Typography
                    variant="h4"
                    sx={{
                        textAlign: 'center',
                        mb: 1,
                        fontWeight: 600,
                        color: theme.palette.text.primary
                    }}
                >
                    {title}
                </Typography>
            )}

            <Box
                sx={{
                    width: '100%',
                    height: typeOfChart === 'pie' ? (compact ? 320 : { xs: 620, sm: 540, md: 500 }) : 'auto',
                    outline: 'none',
                    '& .recharts-surface': { outline: 'none' }
                }}
            >
                {typeOfChart === 'bar' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {barDataset.map((datasetItem, dIndex) => {
                            const hasAnyValues = classes.some((key) => (datasetItem[key] || 0) > 0);

                            if (!hasAnyValues) return null;

                            return (
                                <Box key={`section-${dIndex}`}>
                                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, color: theme.palette.text.primary }}>
                                        {datasetItem.displayName}
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                        {classes.map((key, index) => {
                                            const value = datasetItem[key] || 0;

                                            if (value === 0) return null;

                                            const color = palette?.[index % (palette?.length || 1)] || theme.palette.primary.main;
                                            const IconComponent = iconMapping[key] || DashboardOutlinedIcon;

                                            return (
                                                <Grid item xs={12} sm={6} md={4} lg={3} key={`card-${key}`}>
                                                    <Box
                                                        onClick={() => {
                                                            const payloadItem = { ...datasetItem, value, statusKey: key, name: t(key) };
                                                            handleClick(payloadItem, index, key);
                                                        }}
                                                        sx={{
                                                            p: 1.25,
                                                            border: `1px solid ${theme.palette.divider}`,
                                                            borderRadius: 2,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1.5,
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease-in-out',
                                                            bgcolor: theme.palette.background.paper,
                                                            '&:hover': {
                                                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                                borderColor: color,
                                                                transform: 'translateY(-2px)'
                                                            }
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: 2,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                bgcolor: alpha(color, 0.1),
                                                                color: color
                                                            }}
                                                        >
                                                            <IconComponent fontSize="small" />
                                                        </Box>
                                                        <Stack direction="column">
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: theme.palette.text.secondary,
                                                                    fontWeight: 600,
                                                                    fontSize: '0.75rem'
                                                                }}
                                                            >
                                                                {t(key)}
                                                            </Typography>
                                                            <Typography
                                                                variant="subtitle1"
                                                                sx={{ fontWeight: 800, color: theme.palette.text.primary, lineHeight: 1.1 }}
                                                            >
                                                                {value}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart style={{ outline: 'none' }}>
                            <Pie
                                data={pieDataset}
                                cx="50%"
                                cy="45%"
                                innerRadius={compact ? 65 : 90}
                                outerRadius={compact ? 105 : 140}
                                paddingAngle={5}
                                dataKey="value"
                                nameKey="name"
                                onClick={(d) => handleClick(d)}
                                style={{ outline: 'none' }}
                                activeShape={{ cursor: 'pointer' }}
                                tabIndex="-1"
                            >
                                {pieDataset.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={palette?.[index % (palette?.length || 1)] || theme.palette.primary.main}
                                        style={{ outline: 'none' }}
                                        tabIndex="-1"
                                    />
                                ))}
                            </Pie>
                            <RechartsTooltip />
                            <text
                                x="50%"
                                y="45%"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                style={{
                                    fontSize: compact ? '14px' : '18px',
                                    fontWeight: 'bold',
                                    fill: theme.palette.text.primary,
                                    pointerEvents: 'none'
                                }}
                            >
                                {t(pieCenterLabel)}
                            </text>
                            <Legend
                                verticalAlign="bottom"
                                align="center"
                                iconType="circle"
                                wrapperStyle={{
                                    fontSize: compact ? '12px' : '14px',
                                    paddingTop: compact ? 8 : 0
                                }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                )}
            </Box>
        </Box>
    );
};

export default StackedBarChartCard;
